import { NumberUtils } from './NumberUtils'

type Nested =
  | {
      [key: string]: string | Nested | undefined
    }
  | string[]
  | Nested[]

const getArrayIndex = (path: string) => {
  const isWrapped = path.startsWith('[') && path.endsWith(']')
  if (!isWrapped) {
    return null
  }
  return NumberUtils.parse.toPositiveIntOrNull(path.slice(1, -1))
}

/**
 * Gets the value at Nested path
 * If Nested is array and path is wrapped in brackets, it will try to parse the path as number
 */
const getCurrent = (nested: Nested, path: string) => {
  if (Array.isArray(nested)) {
    const parsed = getArrayIndex(path)
    if (!parsed) {
      return undefined
    }
    return nested[parsed]
  }
  return nested[path]
}

type Value = string | Nested

/**
 * Assigns to a given path on a Nested
 * if Nested is an array and path is wrapped in brackets, it parses path as int and sets the value on index
 * else it sets the path as key
 *
 * Mutates the nested object
 *
 * returns the value
 */
function assign<T extends Value>(nested: Nested, path: string, value: T): T {
  if (Array.isArray(nested)) {
    const parsed = getArrayIndex(path)

    if (typeof parsed !== 'number') {
      return value
    }
    nested[parsed] = value
    return value
  }
  nested[path] = value
  return value
}

/**
 * Sets a value on a nested object given a path as string array
 *
 * @example
 * set({}, ['a', 'b', 'c'], 'd')
 * // { a: { b: { c: 'd' } } }
 *
 * set({}, ['a', '[0]'], 'd')
 * // { a: ['d'] }
 *
 */
export const set = (nested: Nested, path: string[], value: string) => {
  if (typeof nested === 'string') {
    return
  }
  if (path.length === 0) {
    return
  }

  let current = getCurrent(nested, path[0])

  // If we are at the end of the path, set the value
  if (path.length === 1) {
    assign(nested, path[0], value)
    return
  }
  if (typeof current === 'string') {
    console.warn(path, 'Overwriting existing value with object')
  }

  // Create nested object if it doesn't exist and reference it to current
  if (typeof current !== 'object') {
    const nextPath = path[1]
    const nextIsArr = getArrayIndex(nextPath) != null
    current = assign(nested, path[0], nextIsArr ? [] : {})
  }
  // At this point we should always have current
  if (!current) {
    return
  }

  // Recurse
  set(current, path.slice(1), value)
}
